<template>
  <div :class="{ 'kk-arrow': true, 'kk-arrow--parallel': isParallelArrow }">
    <div
      v-if="ready && showArrow"
      :id="`kk-arrow__arrow${arrow.id}`"
      :class="{
        'kk-arrow__arrow': true,
        'kk-arrow__arrow--dragging': stateSnapshot,
      }"
      :style="{ left: `${left}px`, width: `${width}px` }"
    >
      <div
        :class="{
          'kk-arrow__arrow-side': true,
          'kk-arrow__arrow-left-side': true,
        }"
        @mousedown="startResize"
      >
        <span></span>
        <div v-if="isRightToLeft" class="kk-arrow__arrow-left-side-arrowhead">
          <div class="kk-arrow__arrow-left-side-triangle"></div>
          <div v-if="!sameStartEnd && selected && arrowHead" class="kk-arrow__circle"></div>
        </div>
        <div v-else class="kk-arrow__arrow-left-side-linehead">
          <div v-if="!sameStartEnd && selected && !arrowHead" class="kk-arrow__circle"></div>
        </div>
      </div>

      <div class="kk-arrow__arrow-body"></div>
      <div
        :class="{
          'kk-arrow__arrow-side': true,
          'kk-arrow__arrow-right-side': true,
        }"
        @mousedown="startResize"
      >
        <span></span>
        <div v-if="!isRightToLeft" class="kk-arrow__arrow-right-side-arrowhead">
          <!-- Same start end triangle is works as right side triangle -->
          <div
            :class="{
              'kk-arrow__arrow-same-startend-triangle': !stateSnapshot && sameStartEnd,
              'kk-arrow__arrow-right-side-triangle': stateSnapshot || !sameStartEnd,
            }"
          ></div>
          <div
            v-if="sameStartEnd && selected"
            class="kk-arrow__circle"
            :class="{ 'kk-arrow__circle_same-startend': sameStartEnd }"
          ></div>
          <div v-if="!sameStartEnd && selected && arrowHead" class="kk-arrow__circle"></div>
        </div>
        <div v-else class="kk-arrow__arrow-right-side-linehead">
          <div v-if="!sameStartEnd && selected && !arrowHead" class="kk-arrow__circle"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// -- arrow direction:
//    1. minus -> positive,
//    2. minus -> minus,
//    3. positive -> positive
import DesignerMixin from "../../DesignerMixin";
import { ArrowTypes, RowNumberWidth } from "../../../../utils/constants";
export default {
  mixins: [DesignerMixin],
  props: ["rowId", "arrow", "getPxFromRowIndex", "getPxFromColumnIndex", "getIndexAtColumnPx", "getIndexAtRowPx"],
  data() {
    return {
      ready: true,
      left: 0,
      width: 0,
      isRightToLeft: false,
      // the state before each drag-start
      stateSnapshot: null,
      // events coming from ArrowsContainer should only be used by arrow which is active (where mouse down happened)
      isActive: false,
    };
  },
  computed: {
    draggingArrow() {
      return this.$store.state.cells.draggingArrow;
    },
    showArrow() {
      return this.getColumnVisibility(this.arrow.startPlayerId) && this.getColumnVisibility(this.arrow.endPlayerId);
    },
    gridStyle() {
      return this.current.type == "posting" ? "cell" : "track";
    },
    isCellGrid() {
      return this.gridStyle == "cell";
    },
    isParallelArrow() {
      return this.arrow.type == ArrowTypes.Parallel;
    },
    sameStartEnd() {
      return this.arrow.startPlayerId == this.arrow.endPlayerId;
    },
    selected() {
      return this.$store.state.cells.selectedArrow?.id === this.arrow.id;
    },
    arrowHead() {
      return this.$store.state.cells.selectedArrow?.arrowHead;
    },
    arrows() {
      return this.$store.state.cells.current.design.arrows;
    },
  },
  watch: {
    draggingArrow: function (newVal, oldVal) {
      if (newVal.arrowId === this.arrow.id && newVal.x) {
        const newX = this.getPositionOnCanvas(newVal.x);
        this.resize(Math.max(0, newX));
      }
      //  on mouse up
      if (oldVal.arrowId === this.arrow.id && newVal.arrowId === null) {
        this.endResize();
      }
    },
    columns: function (newVal, oldVal) {
      // if there is structural change , column added, removed or replaced
      if (newVal.length !== oldVal.length || newVal.some((itemId, index) => oldVal[index] !== itemId)) {
        this.positionArrow();
      }
    },
    rows: function (newVal, oldVal) {
      // if there is structural change , row added, removed or replaced
      if (newVal.length !== oldVal.length || newVal.some((itemId, index) => oldVal[index] !== itemId)) {
        this.positionArrow();
      }
    },
    arrows: function (newVal, oldVal) {
      this.positionArrow();
    },
    redrawCanvas: function (newVal, oldVal) {
      this.positionArrow();
    },
    isDesignModeActive: function (newVal, oldVal) {
      this.$nextTick(() => {
        this.positionArrow();
      });
    },
  },
  mounted() {
    this.positionArrow();
  },
  methods: {
    startResize($event) {
      this.isActive = true;
      this.$store.dispatch("cells/setDraggingArrow", { arrowId: this.arrow.id });

      if (!this.readonly && $event.which == 1) {
        let element = $event.target;
        const clazz = "kk-arrow__arrow-side";
        if (!element.classList.contains(clazz)) {
          element = element.closest(`.${clazz}`);
        }

        // take a snapshot of the state
        this.stateSnapshot = {
          left: this.left,
          width: this.width,
          toLeft: element.classList.contains("kk-arrow__arrow-left-side"),
          toRight: element.classList.contains("kk-arrow__arrow-right-side"),
          isRightToLeft: this.isRightToLeft,
        };
      }
    },

    async endResize() {
      if (!this.isActive) return;

      this.isActive = false;
      if (!this.readonly) {
        const arrowElBound = this.getBound(`kk-arrow__arrow${this.arrow.id}`);
        if (!arrowElBound) return;

        let startPlayerPx = this.getPositionOnCanvas(arrowElBound.left);
        let startPlayerId = this.getPlayerAt(startPlayerPx);
        let endPlayerPx = this.getPositionOnCanvas(arrowElBound.right);
        let endPlayerId = this.getPlayerAt(endPlayerPx);

        const plusToMinus = startPlayerId?.includes("Plus") && endPlayerId?.includes("Minus");
        const minusToPlus = endPlayerId?.includes("Plus") && startPlayerId?.includes("Minus");

        if ((this.isRightToLeft && minusToPlus) || (!this.isRightToLeft && plusToMinus)) {
          if (this.stateSnapshot.toRight) {
            endPlayerId = this.getPlayerAt(endPlayerPx - this.getRawWidth(endPlayerId));
          } else if (this.stateSnapshot.toLeft) {
            startPlayerId = this.getPlayerAt(startPlayerPx + this.getRawWidth(startPlayerId));
          }
        }

        if (!startPlayerId || !endPlayerId || this.columnMap[startPlayerId].type !== "player")
          return this.positionArrow();

        // swap start/end if arrow is right to left
        if (this.isRightToLeft) {
          const id = startPlayerId;
          startPlayerId = endPlayerId;
          endPlayerId = id;
        }

        await this.$store.dispatch("cells/updateArrow", {
          rowId: this.rowId,
          id: this.arrow.id,
          startPlayerId,
          endPlayerId,
        });

        this.positionArrow();
        this.stateSnapshot = null;
      }
    },
    resize(changingX) {
      if (!this.readonly && this.stateSnapshot) {
        const mouseLeft = changingX;
        // --- update this.left, this.width, and this.isRightToLeft --- \\

        // when the right side of arrow is dragging
        if (this.stateSnapshot.toRight) {
          // when arrow is on right
          if (mouseLeft > this.stateSnapshot.left) {
            this.width = mouseLeft - this.stateSnapshot.left;
            this.isRightToLeft = this.stateSnapshot.isRightToLeft;
          }
          // when arrow is on the left
          else {
            this.left = mouseLeft;
            this.width = this.stateSnapshot.left - mouseLeft;
            this.isRightToLeft = !this.stateSnapshot.isRightToLeft;
          }
        }
        // when the left side of arrow is dragging
        else if (this.stateSnapshot.toLeft) {
          const right = this.stateSnapshot.left + this.stateSnapshot.width;
          // when arrow is on the left
          if (mouseLeft < right) {
            this.left = mouseLeft;
            this.width = right - mouseLeft;
            this.isRightToLeft = this.stateSnapshot.isRightToLeft;
          }
          // when arrow is on the right
          else {
            this.left = right;
            this.width = mouseLeft - right;
            this.isRightToLeft = !this.stateSnapshot.isRightToLeft;
          }
        }
      }
    },
    getBound(elId) {
      const el = document.getElementById(elId);
      return el ? el.getBoundingClientRect() : null;
    },
    getPlayerAt(px) {
      const index = this.getIndexAtColumnPx(px);
      return this.columns[index]?.id;
    },
    positionArrow() {
      // in track-mode: always minus -> plus
      // in cell-mode (simple arrow): always minus -> plus
      // in cell-mode (parallel arrow): start and end must be the same
      // so, -->
      // const startColumn = this.isParallelArrow
      //   ? this.activity.startColumn || PostingCellTypes.Minus
      //   : PostingCellTypes.Minus;
      // const endColumn = this.isParallelArrow ? startColumn : PostingCellTypes.Plus;

      const startColumnId = this.arrow.startPlayerId;
      const startColumnIndex = this.columnMap[startColumnId].index;
      const startColumnLeft = this.getPxFromColumnIndex(startColumnIndex);
      const startColumnWidth = this.getRawWidth(startColumnId);

      const endColumnId = this.arrow.endPlayerId;
      const endColumnIndex = this.columnMap[endColumnId].index;
      const endColumnLeft = this.getPxFromColumnIndex(endColumnIndex);
      const endColumnWidth = this.getRawWidth(endColumnId);

      // abort if either start/end doesn't exists
      this.ready = startColumnWidth && endColumnWidth;

      if (!this.ready) return;
      this.isRightToLeft = !this.sameStartEnd && startColumnLeft > endColumnLeft; // with 90deg rotation it should be left to right

      let side1 = startColumnLeft + startColumnWidth / 2;
      let side2 = endColumnLeft + endColumnWidth / 2;

      if (!this.sameStartEnd && this.getCellValue(this.rowMap[this.rowId].index, this.columnMap[startColumnId].index)) {
        side1 = this.isRightToLeft ? startColumnLeft + startColumnWidth / 4 : startColumnLeft + startColumnWidth / 1.2;
      }
      if (!this.sameStartEnd && this.getCellValue(this.rowMap[this.rowId].index, this.columnMap[endColumnId].index)) {
        side2 = this.isRightToLeft ? endColumnLeft + endColumnWidth / 1.2 : endColumnLeft + endColumnWidth / 4;
      }

      if (startColumnId.includes("Plus") && endColumnId.includes("Minus")) {
        side2 -= endColumnWidth;
      }

      let left = Math.min(side1, side2);
      let width = Math.max(side1, side2) - left;

      // if (this.sameStartEnd && this.isCellGrid && !this.isParallelArrow) {
      //   // in such case, the above code treat the plus and minus as two diff columns
      //   // so when dragging again, the arrow start end is located under plus column
      //   // which is wrong. the solution is to use the minus column as the start & the end.
      //   left += width;
      //   width = 0.001;
      // }

      // setting same calculated value will not force vue-resizable to update arrow position
      // 'delta' is a small value to fix this
      const delta = Math.random() * 0.000001;
      this.left = left + delta;
      this.width = width + delta;
    },
    getPositionOnCanvas(px) {
      const tableContent = document.querySelector(".table-content");
      const tableContentBound = tableContent?.getBoundingClientRect();
      return px - tableContentBound.left - RowNumberWidth + tableContent.scrollLeft;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../../assets/styles/variables";

.kk-arrow {
  position: relative;
  z-index: 15;
  height: 0;
  &__circle {
    width: 1rem;
    height: 1rem;
    position: absolute;
    border: 3px solid @kk-color-gray;
    border-radius: 50%;
    &_same-startend {
      left: -1.5rem;
    }
  }
  &__arrow {
    position: absolute;
    display: flex;
    align-items: center;
    transition: width 0.3s ease, left 0.3s ease;
  }
  &__arrow-body {
    border-bottom: 0.16rem solid @kk-color-primary-blue;
    flex-grow: 1;
  }

  &__arrow-left-side,
  &__arrow-right-side {
    position: relative;
    width: 2rem;
    border-bottom: 0.16rem solid @kk-color-primary-blue;
    cursor: grab;
    // enlarge arrow activation area
    // Ref: https://stackoverflow.com/questions/34709029/increasing-hover-area-of-css-circle
    // Ref: https://stackoverflow.com/questions/11998593/can-i-have-multiple-before-pseudo-elements-for-the-same-element#:~:text=In%20CSS2.,than%20one%20of%20each%20kind.)
    // Hydra Orc answer
    span {
      &::before {
        content: "";
        position: absolute;
        border-radius: 50%;
        width: 2rem;
        height: 1.938rem;
        left: 50%;
        top: 50%;
        transform: translate(-35%, -50%);
        z-index: 1;
      }
    }
  }
  // Parallel design make arrow color gray
  &--parallel &__arrow-body {
    border-bottom-color: @color-grey-dark-2;
  }

  &--parallel &__arrow-left-side,
  &--parallel &__arrow-right-side {
    border-bottom-color: @color-grey-dark-2;
  }

  &--parallel &__arrow-right-side-triangle {
    border-left-color: @color-grey-dark-2;
  }
  &--parallel &__arrow-left-side-triangle {
    border-right-color: @color-grey-dark-2;
  }
  &--parallel &__arrow-same-startend-triangle {
    border-top-color: @color-grey-dark-2;
  }

  &__arrow-left-side {
    span {
      &::before {
        transform: translate(-70%, -50%);
      }
    }
    & .kk-arrow__circle {
      transform: translate(-100%, -50%);
    }
  }
  &__arrow--dragging {
    transition: none;
  }
  &__arrow--dragging &__arrow-body,
  &__arrow--dragging &__arrow-left-side,
  &__arrow--dragging &__arrow-right-side {
    border-bottom-color: @kk-color-primary-blue;
  }
  &__arrow--dragging &__arrow-right-side-triangle {
    border-left-color: @kk-color-primary-blue;
  }
  &__arrow--dragging &__arrow-left-side-triangle {
    border-right-color: @kk-color-primary-blue;
  }
  &__arrow--dragging &__arrow-same-startend-triangle {
    border-top-color: @kk-color-primary-blue;
  }

  &__arrow-right-side-triangle,
  &__arrow-left-side-triangle,
  &__arrow-same-startend-triangle {
    position: absolute;
    top: 0px;
    width: 0;
    height: 0;
  }
  &__arrow-right-side-triangle {
    right: 0px;
    border-top: 5px solid transparent;
    border-left: 5px solid @kk-color-primary-blue;
    border-bottom: 5px solid transparent;
    transform: translate(50%, calc(-50% + @kk-arrow-height / 2));
  }
  &__arrow-left-side-triangle {
    left: 0px;
    border-top: 5px solid transparent;
    border-right: 5px solid @kk-color-primary-blue;
    border-bottom: 5px solid transparent;
    transform: translate(-50%, calc(-50% + @kk-arrow-height / 2));
  }
  &__arrow-same-startend-triangle {
    left: 0px;
    border: 0px solid transparent;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid @kk-color-primary-blue;
    transform: translateX(-50%);
  }
  &__arrow-left-side-linehead,
  &__arrow-left-side-arrowhead,
  &__arrow-right-side-linehead,
  &__arrow-right-side-arrowhead {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: @kk-arrow-height;
  }
  &__arrow-right-side {
    &:hover &-arrowhead,
    &:hover &-linehead {
      background-color: @kk-color-gold;
    }
    &:hover &-triangle {
      border-left-color: @kk-color-gold;
    }
    &:hover .kk-arrow__arrow-same-startend-triangle {
      border-top-color: @kk-color-gold;
    }
    & .kk-arrow__circle {
      right: 0;
      transform: translate(100%, -50%);
    }
  }
  &__arrow-left-side {
    &:hover &-arrowhead,
    &:hover &-linehead {
      background-color: @kk-color-gold;
    }
    &:hover &-triangle {
      border-right-color: @kk-color-gold;
    }
  }
}
</style>
